// Main-thread rAF jitter sampler for the layer-thumbnail back-off tier
// (layer-thumbnails-2026-10-13.md 追加: 遅い端末では段階的に後回し)。
//
// `longtask` is not available on Safari, so "is the main thread struggling?" is
// measured as: share of rAF frames more than `SLOW_FRAME_MS` apart. To stay
// cheap it is NOT a permanent rAF loop: every `PERIOD_MS` it runs one short
// burst of `BURST_FRAMES` frames and reports the ratio. A burst is thrown away
// if the user's own pen goes down (we only want idle-time numbers) or the tab
// is hidden. No React, no state outside this class.

export const JANK_SLOW_FRAME_MS = 48;
export const JANK_PERIOD_MS = 5000;
export const JANK_BURST_FRAMES = 30;
/** A gap this long is a paused tab / sleep, not jank. */
const JANK_IGNORE_GAP_MS = 1000;

export interface JankSamplerDeps {
  /** True while the user's own pen is down. */
  readonly isDrawing: () => boolean;
  readonly report: (ratio: number) => void;
  readonly raf?: (cb: (t: number) => void) => number;
  readonly cancelRaf?: (h: number) => void;
  readonly setTimer?: (fn: () => void, ms: number) => unknown;
  readonly clearTimer?: (h: unknown) => void;
  readonly isHidden?: () => boolean;
}

export class ThumbnailJankSampler {
  private running = false;
  private timer: unknown = null;
  private rafHandle: number | null = null;
  private last: number | null = null;
  private frames = 0;
  private slow = 0;
  private readonly raf: (cb: (t: number) => void) => number;
  private readonly cancelRaf: (h: number) => void;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (h: unknown) => void;
  private readonly isHidden: () => boolean;

  constructor(private readonly deps: JankSamplerDeps) {
    this.raf = deps.raf ?? ((cb) => requestAnimationFrame(cb));
    this.cancelRaf = deps.cancelRaf ?? ((h) => cancelAnimationFrame(h));
    this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = deps.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
    this.isHidden =
      deps.isHidden ??
      (() => typeof document !== 'undefined' && document.visibilityState === 'hidden');
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.scheduleBurst();
  }

  stop(): void {
    this.running = false;
    if (this.timer !== null) this.clearTimer(this.timer);
    this.timer = null;
    if (this.rafHandle !== null) this.cancelRaf(this.rafHandle);
    this.rafHandle = null;
  }

  private scheduleBurst(): void {
    this.timer = this.setTimer(() => {
      this.timer = null;
      if (!this.running) return;
      if (this.deps.isDrawing() || this.isHidden()) {
        this.scheduleBurst();
        return;
      }
      this.frames = 0;
      this.slow = 0;
      this.last = null;
      this.rafHandle = this.raf((t) => this.frame(t));
    }, JANK_PERIOD_MS);
  }

  private frame(t: number): void {
    this.rafHandle = null;
    if (!this.running) return;
    if (this.deps.isDrawing() || this.isHidden()) {
      this.scheduleBurst(); // discard: only idle-time numbers count
      return;
    }
    if (this.last !== null) {
      const dt = t - this.last;
      if (dt < JANK_IGNORE_GAP_MS) {
        this.frames++;
        if (dt > JANK_SLOW_FRAME_MS) this.slow++;
      }
    }
    this.last = t;
    if (this.frames >= JANK_BURST_FRAMES) {
      this.deps.report(this.slow / this.frames);
      this.scheduleBurst();
      return;
    }
    this.rafHandle = this.raf((nt) => this.frame(nt));
  }
}
